import AuthCodeOtpInput from "@casino/legacy/client/components/2faManaging/AuthCodeOtpInput";
import SubmitLogin from "@casino/legacy/client/components/Auth/Shared/SubmitLogin";
import FormCaptcha from "@casino/legacy/client/shared/form/FormCaptcha";
import Translate from "@casino/legacy/client/shared/i18n/Translate";
import FlexStack from "@casino/legacy/client/shared/styles/FlexStack";
import FormCheckbox from "@casino/legacyUi/form/FormCheckbox";
import FormError from "@casino/legacyUi/form/FormError";
import FormInput from "@casino/legacyUi/form/FormInput";
import Dialog from "@casino/legacyUi/TwoFactorDialog";
import { getCookie } from "@workspace/cookies/browser";
import ResendCodeButton from "./ResendCodeButton";
import useFormLogin from "./useFormLogin";

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};

const FormLogin: FC<Props> = ({ onSuccess, isPopover = false }) => {
  const prefilledEmail = getCookie("existingEmail");
  const {
    uses2FA,
    errors,
    loading,
    control,
    handleSubmit,
    isSubmitDisabled,
    captchaRequired,
    required,
    recaptchaRef,
    authenticatorLabelCheckbox,
    authenticatorLabelCode,
    phoneHint,
    needsSms2fa,
    handleDialogClose,
    handleCodeSubmit,
    authCodeErrorMessage,
    handleResendCodeClick,
    clearErrors,
    handleBlur,
  } = useFormLogin({
    isPopover,
    onSuccess,
    defaultValues: { username: prefilledEmail },
  });

  return (
    <form>
      <div className="grid gap-2 list-none">
        <FormInput
          {...required("username")}
          type="text"
          onBlur={handleBlur}
          label="profile.email_username"
          autoCapitalize="off"
          error={errors.email?.message ?? errors.username?.message}
          className="fs-mask"
        />
        <FormInput
          {...required("password")}
          onBlur={handleBlur}
          type="password"
          label="profile.password"
          error={errors.password?.message}
          className="fs-mask"
        />
        <FormCheckbox
          {...required("uses2FA", false)}
          checked={uses2FA}
          label={authenticatorLabelCheckbox}
        />
        {uses2FA && (
          <FormInput
            type="tel"
            label={authenticatorLabelCode}
            autoComplete="off"
            error={errors.otp?.message}
            {...required("otp", uses2FA)}
          />
        )}
        {captchaRequired && (
          <FormCaptcha
            ref={recaptchaRef}
            name="captcha"
            control={control}
            error={errors.captcha?.message}
            rules={{ required: captchaRequired && "errors.input.required" }}
          />
        )}
        <SubmitLogin
          loading={loading}
          handleSubmit={handleSubmit}
          disabled={isSubmitDisabled}
        />
        {errors?.global?.message && (
          <FormError
            message={errors.global.message}
            data-testid={errors?.global?.message}
          />
        )}
        {needsSms2fa && (
          <Dialog
            onClose={handleDialogClose}
            title="profile.verify_phone_number"
          >
            <p className="text-sm leading-6 text-muted-foreground">
              <Translate
                id="profile.enter_code_from_sms"
                values={{ phone: phoneHint }}
              />
            </p>
            <AuthCodeOtpInput
              onSubmit={handleCodeSubmit}
              onChange={() => clearErrors()}
              errorMessage={authCodeErrorMessage}
              data-testid="sms-code-field"
            />
            <FlexStack inline gridGap="4px" alignItems="center">
              <p className="text-sm leading-6 text-muted-foreground">
                <Translate id="profile.did_not_get_code" />
              </p>
              <ResendCodeButton onResendCodeClick={handleResendCodeClick} />
            </FlexStack>
          </Dialog>
        )}
      </div>
    </form>
  );
};

export default FormLogin;
